// A failed action's toast (ported from vouchers' toastMutationError): the
// action as the title, friendlyMessage's words below — never a raw server
// line, code or HTML page — and the request id for support when there is one.

import { toast } from "@pej/kore/components";
import type { FieldValues, UseFormReturn } from "react-hook-form";
import { ApiError, type FieldErrorSpec } from "@/api/error";
import { applyProblemDetails } from "@/lib/forms";
import { ErrorToastContent } from "./error-toast-content";

/** E.g. `toastMutationError("Save failed", err)`; `unmapped` lists field
 *  errors no field shows. */
export function toastMutationError(
    title: string,
    error: unknown,
    opts?: { readonly unmapped?: ReadonlyArray<FieldErrorSpec> },
) {
    toast.error(title, {
        description: <ErrorToastContent error={error} unmapped={opts?.unmapped} />,
        // The server answered: sticky, so its request id can be copied.
        // Anything else has nothing to copy, so it goes like any toast.
        duration: error instanceof ApiError ? Number.POSITIVE_INFINITY : 6000,
    });
}

/** A form's failed save: field errors on their fields, and a toast only for
 *  what no field shows — a problem without field errors, or the ones that
 *  didn't map, listed. True when every error landed on a field (no toast),
 *  so a save bar can say the summary. */
export function toastFormError<TFieldValues extends FieldValues>(
    title: string,
    form: UseFormReturn<TFieldValues, unknown, unknown>,
    error: ApiError,
): boolean {
    const unmapped = applyProblemDetails(form, error);
    if (error.isValidation && unmapped.length === 0) return true;
    toastMutationError(title, error, { unmapped });
    return false;
}
